import React from "react";
import { Action as ReduxAction } from "redux";
import actionCreatorFactory from "../util/redux/action-factory";
import { reducerWithInitialState } from "../util/redux/typed-reducers";
import { env } from "../util/env";
import { ReduxState, ReduxDispatch } from "../ducks/reducers";
import {
    AMLRiskAssesmentClassDto,
    AmlRiskAssesmentStatus,
    IProfileDataDto,
} from "../util/DTOs/ProfileDtos";
import * as request from "../util/request";
import { showSnackbar } from "./snackbar";

const actionFactory = actionCreatorFactory("HypoMobiili/Profile");
const saveProfileAction = actionFactory<
    IProfileDataDto,
    IProfileDataDto,
    string
>("SAVE_PROFILE");
const getProfileAction = actionFactory<null, IProfileDataDto, string>(
    "GET_PROFILE"
);
const toggleEditProfileAction = actionFactory("TOGGLE_EDIT_PROFILE");
const getAmlRiskAssesmentClassAction = actionFactory<
    null,
    AMLRiskAssesmentClassDto,
    string
>("GET_AML_CLASS");

const getAmlRiskAssesmentStatusAction = actionFactory<
    null,
    AmlRiskAssesmentStatus,
    string
>("GET_AML_STATUS");

export function saveProfileData(data: IProfileDataDto) {
    return async (dispatch: ReduxDispatch): Promise<IProfileDataDto> => {
        try {
            dispatch(saveProfileAction(data));

            const result = await request.put<IProfileDataDto>(
                `${env.APP_API_HOST}/api/profile`,
                data
            );
            dispatch(saveProfileAction.done(result));
            return result;
        } catch (err) {
            dispatch(saveProfileAction.failed(""));
            dispatch(showSnackbar("Tallennus epäonnistui"));
            throw err;
        }
    };
}

export function fetchProfileData() {
    return async (dispatch: ReduxDispatch): Promise<IProfileDataDto> => {
        try {
            dispatch(getProfileAction(null));

            const result = await request.get<IProfileDataDto>(
                `${env.APP_API_HOST}/api/profile`
            );
            dispatch(getProfileAction.done(result));
            return result;
        } catch (err) {
            dispatch(getProfileAction.failed(err));
            throw err;
        }
    };
}

export function toggleEditProfile() {
    return (dispatch: ReduxDispatch) => {
        dispatch(toggleEditProfileAction());
    };
}

export function getAmlClass() {
    return async (
        dispatch: ReduxDispatch
    ): Promise<AMLRiskAssesmentClassDto> => {
        try {
            dispatch(getAmlRiskAssesmentClassAction(null));
            const result = await request.get<AMLRiskAssesmentClassDto>(
                `${env.APP_API_HOST}/api/profile/aml`
            );
            dispatch(getAmlRiskAssesmentClassAction.done(result));
            return result;
        } catch (err) {
            dispatch(getAmlRiskAssesmentClassAction.failed(err));
            throw err;
        }
    };
}

export function getAmlStatus() {
    return async (dispatch: ReduxDispatch): Promise<AmlRiskAssesmentStatus> => {
        try {
            dispatch(getAmlRiskAssesmentStatusAction(null));
            const result = await request.get<AmlRiskAssesmentStatus>(
                `${env.APP_API_HOST}/api/profile/aml/status`
            );
            dispatch(getAmlRiskAssesmentStatusAction.done(result));
            return result;
        } catch (err) {
            dispatch(getAmlRiskAssesmentStatusAction.failed(err));
            throw err;
        }
    };
}

export interface ProfileState extends IProfileDataDto {
    loading: boolean;
    editProfile: boolean;
    iOrderBankPostAsPaperPost?: boolean;
    aml?: AMLRiskAssesmentClassDto;
    amlStatus?: AmlRiskAssesmentStatus;
}

const initialProfileState: ProfileState = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    streetAddress: "",
    postalCode: "",
    loading: false,
    hasLoginCredentials: false,
    editProfile: false,
    aml: undefined,
    iOrderBankPostAsPaperPost: false,
};

export const ProfileReducer = reducerWithInitialState(initialProfileState)
    .case(saveProfileAction, (state, action) => {
        return {
            ...state,
            email: action.payload.email,
            phoneNumber: action.payload.phoneNumber,
            streetAddress: action.payload.streetAddress,
            postalCode: action.payload.postalCode,
            iOrderBankPostAsPaperPost: action.payload.iOrderBankPostAsPaperPost,
            loading: true,
        };
    })
    .case(saveProfileAction.done, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(saveProfileAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(getProfileAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(getProfileAction.done, (state, action) => {
        return {
            ...state,
            ...action.payload,
            loading: false,
        };
    })
    .case(getProfileAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(toggleEditProfileAction, (state, action) => {
        return {
            ...state,
            editProfile: !state.editProfile,
        };
    })
    .case(getAmlRiskAssesmentClassAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(getAmlRiskAssesmentClassAction.done, (state, action) => {
        return {
            ...state,
            aml: action.payload,
            loading: false,
        };
    })
    .case(getAmlRiskAssesmentClassAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    })
    .case(getAmlRiskAssesmentStatusAction, (state, action) => {
        return {
            ...state,
            loading: true,
        };
    })
    .case(getAmlRiskAssesmentStatusAction.done, (state, action) => {
        return {
            ...state,
            amlStatus: action.payload,
            loading: false,
        };
    })
    .case(getAmlRiskAssesmentStatusAction.failed, (state, action) => {
        return {
            ...state,
            loading: false,
        };
    });

export default ProfileReducer;



// WEBPACK FOOTER //
// ./src/ducks/profile.ts